import { useState } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { Search, Book, MessageCircle, FileText, Plus, X, Edit2, Trash2, Mail, Phone, ChevronUp, ChevronDown } from 'lucide-react';
import { useOrders } from '../../hooks/useOrders';
import { toast } from 'sonner';

interface HelpPageProps {
  onNavigate?: (page: string) => void;
}

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
}

export function HelpPage({ onNavigate }: HelpPageProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { orders: ordersData } = useOrders({ page: 1, limit: 50 });
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showAddFAQModal, setShowAddFAQModal] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState<FAQ | null>(null);

  const [faqs, setFaqs] = useState<FAQ[]>([
    {
      id: '1',
      question: 'How do I add a new menu item?',
      answer: 'Navigate to the Menus page from the sidebar, select the category you want to add the item to, and click the "Add Item" button. Fill in the required details such as name, price, description, and upload an image. Click "Save" to add the item to your menu.',
      category: 'Menu Management',
    },
    {
      id: '2',
      question: 'How can I track my orders in real-time?',
      answer: 'Go to the Orders page from the sidebar. You\'ll see all orders with their current status. Orders are automatically updated in real-time. You can filter by status (pending, completed, cancelled) and click on any order to view detailed information.',
      category: 'Orders',
    },
    {
      id: '3',
      question: 'How do I respond to customer reviews?',
      answer: 'Visit the Reviews page from the sidebar. Each review has a "Reply" button. Click it to open a reply form where you can compose your response. Your reply will be visible to the customer and helps build trust with your audience.',
      category: 'Reviews',
    },
    {
      id: '4',
      question: 'Can I export my analytics data?',
      answer: 'Yes! On the Analytics page, click the "Export" button in the top right corner. You can choose to export data as CSV, Excel, or PDF format. Select your date range and the specific metrics you want to include in the export.',
      category: 'Analytics',
    },
    {
      id: '5',
      question: 'How do I manage customer membership programs?',
      answer: 'Navigate to Customers > Members from the sidebar. You can view all members, add new members, and manage their benefits. Members automatically receive notifications about promotions and special offers.',
      category: 'Customers',
    },
    {
      id: '6',
      question: 'What should I do if I forget my password?',
      answer: 'Click on the profile icon in the top right corner and select "Profile". Navigate to the Security section where you can change your password. Make sure to use a strong password with at least 8 characters including numbers and special characters.',
      category: 'Account',
    },
    {
      id: '7',
      question: 'How do I create and manage promotions?',
      answer: 'Go to the Promotions page from the top navigation. Click "Add Promotion" to create a new offer. Set the discount amount, validity period, and applicable items. You can track performance and edit or deactivate promotions at any time.',
      category: 'Promotions',
    },
    {
      id: '8',
      question: 'Can I customize the dashboard to show specific metrics?',
      answer: 'Yes! On the Dashboard page, you can customize which widgets and metrics are displayed. Click the settings icon in the top right of each widget to configure its display options. Your preferences are saved automatically.',
      category: 'Dashboard',
    },
    {
      id: '9',
      question: 'How do I enable dark mode?',
      answer: 'Click on the Settings icon in the top navigation bar. Under "Appearance" settings, you\'ll find a toggle for Dark Mode. Your preference will be saved and applied across all pages of the dashboard.',
      category: 'Settings',
    },
    {
      id: '10',
      question: 'What payment methods are supported?',
      answer: 'The system supports all major payment methods including credit/debit cards, digital wallets (Apple Pay, Google Pay), and cash on delivery. You can enable or disable specific payment methods in the Settings > Payment Methods section.',
      category: 'Payments',
    },
    {
      id: '11',
      question: 'How do I add staff members to the system?',
      answer: 'Go to Settings > Staff Management. Click "Add Staff Member" and fill in their details including name, role, and permissions. You can assign different access levels (Admin, Manager, Staff) to control what each person can view and modify.',
      category: 'Settings',
    },
    {
      id: '12',
      question: 'Can I integrate with third-party delivery services?',
      answer: 'Yes! Navigate to Settings > Integrations. We support integration with major delivery platforms like UberEats, DoorDash, and Grubhub. Follow the setup wizard to connect your accounts and sync orders automatically.',
      category: 'Integrations',
    },
  ]);

  const categories = ['all', ...Array.from(new Set(faqs.map(faq => faq.category)))];

  const filteredFAQs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const quickLinks = [
    {
      icon: Book,
      title: 'Documentation',
      description: 'Comprehensive guides and tutorials',
      color: 'bg-blue-500',
    },
    {
      icon: MessageCircle,
      title: 'Community Forum',
      description: 'Connect with other restaurant owners',
      color: 'bg-green-500',
    },
  ];

  const resources = [
    {
      title: 'Getting Started Guide',
      description: 'Learn the basics of setting up your restaurant dashboard',
      icon: FileText,
      link: '#',
    },
    {
      title: 'Best Practices',
      description: 'Tips and tricks for maximizing your restaurant efficiency',
      icon: Book,
      link: '#',
    },
    {
      title: 'API Documentation',
      description: 'Technical documentation for developers and integrations',
      icon: FileText,
      link: '#',
    },
  ];

  const handleAddFAQ = () => {
    const newFAQ: FAQ = {
      id: (faqs.length + 1).toString(),
      question: '',
      answer: '',
      category: 'all',
    };
    setEditingFAQ(newFAQ);
    setShowAddFAQModal(true);
  };

  const handleEditFAQ = (faq: FAQ) => {
    setEditingFAQ(faq);
    setShowAddFAQModal(true);
  };

  const handleSaveFAQ = () => {
    if (!editingFAQ?.question.trim() || !editingFAQ?.answer.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    const existingFAQ = faqs.find(faq => faq.id === editingFAQ.id);
    
    if (existingFAQ) {
      // Edit existing FAQ
      const updatedFaqs = faqs.map(faq => (faq.id === editingFAQ.id ? editingFAQ : faq));
      setFaqs(updatedFaqs);
    } else {
      // Add new FAQ
      setFaqs([...faqs, editingFAQ]);
    }
    
    setEditingFAQ(null);
    setShowAddFAQModal(false);
  };

  const handleDeleteFAQ = (id: string) => {
    const updatedFaqs = faqs.filter(faq => faq.id !== id);
    setFaqs(updatedFaqs);
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(!sidebarOpen)}
        activePage="help"
        onNavigate={onNavigate}
        ordersCount={ordersData.length}
      />
      
      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
        sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'
      }`}>
        <TopBar 
          onMenuClick={() => setSidebarOpen(!sidebarOpen)} 
          sidebarOpen={sidebarOpen} 
          onNavigate={onNavigate}
        />
        
        <main className="flex-1 overflow-y-auto">
          {/* Hero Section */}
          <div className="bg-gray-50 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 py-8 px-6">
            <div className="max-w-[1400px] mx-auto text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-indigo-100 dark:bg-indigo-900/30 rounded-2xl mb-4">
                <Search className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">How can we help you?</h1>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
                Search our knowledge base or browse categories below
              </p>
              
              {/* Search Bar */}
              <div className="relative max-w-2xl mx-auto">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for help articles, FAQs..."
                  className="w-full pl-12 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <div className="max-w-[1400px] mx-auto px-6 py-12">
            {/* Quick Links */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Quick Links</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {quickLinks.map((link, index) => (
                  <button
                    key={index}
                    className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 text-left hover:shadow-lg transition-all hover:-translate-y-1"
                  >
                    <div className={`inline-flex items-center justify-center w-12 h-12 ${link.color} rounded-lg mb-4`}>
                      <link.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{link.title}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{link.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Contact Support */}
            <div className="mb-12">
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-8 text-white">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <h3 className="text-2xl font-bold mb-2">Need Personalized Help?</h3>
                    <p className="text-blue-100">Our support team is available 24/7 to assist you</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button className="flex items-center gap-2 px-6 py-3 bg-white text-indigo-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors">
                      <Mail className="w-5 h-5" />
                      Email Us
                    </button>
                    <button className="flex items-center gap-2 px-6 py-3 bg-indigo-700 text-white rounded-lg font-semibold hover:bg-indigo-800 transition-colors">
                      <Phone className="w-5 h-5" />
                      Call Now
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="mb-12">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Frequently Asked Questions</h2>
                
                <div className="flex items-center gap-3">
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category === 'all' ? 'All Categories' : category}
                      </option>
                    ))}
                  </select>
                  
                  <button
                    onClick={handleAddFAQ}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm font-medium text-sm whitespace-nowrap"
                  >
                    <Plus className="w-4 h-4" />
                    Add FAQ
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                {filteredFAQs.map((faq) => (
                  <div
                    key={faq.id}
                    className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden"
                  >
                    <div className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <button
                        onClick={() => setExpandedFAQ(expandedFAQ === faq.id ? null : faq.id)}
                        className="flex items-start gap-4 flex-1 text-left"
                      >
                        <div className="mt-1">
                          <Search className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{faq.question}</h3>
                          <span className="text-xs px-2 py-1 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400">
                            {faq.category}
                          </span>
                        </div>
                      </button>
                      
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleEditFAQ(faq)}
                          className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                          title="Edit FAQ"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm('Are you sure you want to delete this FAQ?')) {
                              handleDeleteFAQ(faq.id);
                            }
                          }}
                          className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Delete FAQ"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setExpandedFAQ(expandedFAQ === faq.id ? null : faq.id)}
                          className="p-2"
                        >
                          {expandedFAQ === faq.id ? (
                            <ChevronUp className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {expandedFAQ === faq.id && (
                      <div className="px-6 pb-4 border-t border-gray-100 dark:border-gray-800 pt-4">
                        <p className="text-gray-600 dark:text-gray-400 leading-relaxed pl-9">
                          {faq.answer}
                        </p>
                      </div>
                    )}
                  </div>
                ))}

                {filteredFAQs.length === 0 && (
                  <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-12 text-center">
                    <Search className="w-16 h-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No results found</h3>
                    <p className="text-gray-500 dark:text-gray-400">
                      Try adjusting your search or browse different categories
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Resources Section */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Additional Resources</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {resources.map((resource, index) => (
                  <a
                    key={index}
                    href={resource.link}
                    className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 hover:shadow-lg transition-all hover:-translate-y-1"
                  >
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg mb-4">
                      <resource.icon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{resource.title}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{resource.description}</p>
                  </a>
                ))}
              </div>
            </div>

            {/* Still Need Help Section */}
            <div className="mt-12 bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-8 text-center">
              <MessageCircle className="w-12 h-12 text-indigo-600 dark:text-indigo-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Still need help?</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Can't find what you're looking for? Our support team is here to help!
              </p>
              <button className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
                Contact Support
              </button>
            </div>
          </div>
        </main>
      </div>

      {/* Add/Edit FAQ Modal */}
      {showAddFAQModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-8 w-full max-w-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingFAQ ? 'Edit FAQ' : 'Add New FAQ'}
              </h2>
              <button
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                onClick={() => setShowAddFAQModal(false)}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Question</label>
                <input
                  type="text"
                  value={editingFAQ?.question || ''}
                  onChange={(e) => setEditingFAQ({ ...editingFAQ!, question: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Answer</label>
                <textarea
                  value={editingFAQ?.answer || ''}
                  onChange={(e) => setEditingFAQ({ ...editingFAQ!, answer: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Category</label>
                <select
                  value={editingFAQ?.category || 'all'}
                  onChange={(e) => setEditingFAQ({ ...editingFAQ!, category: e.target.value })}
                  className="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category === 'all' ? 'All Categories' : category}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                className="px-6 py-3 bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600 transition-colors"
                onClick={() => setShowAddFAQModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors ml-3"
                onClick={handleSaveFAQ}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}